import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiCalendarEvent, BiCheck } from 'react-icons/bi'
import { getEventTypes, deleteEventType } from '../../actions/ads'
import Li from '../../components/Li'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import EventTypeModal from './Modal'
import styles from './styles.module.scss'

const EventTypes = ({ data, setModalValue, closeSubModal }) => {
  const dispatch = useDispatch()
  const { list, loading } = useSelector(state => state.event_type)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [select, setSelect] = useState(null)

  const isSelectionMode = data === 'eventList'

  useEffect(() => {
    dispatch(getEventTypes())
  }, [dispatch])

  const handleDelete = useCallback((id) => (e) => {
    e.stopPropagation()
    setConfirmation(id)
  }, [])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const handleSelect = useCallback((item) => {
    if (isSelectionMode) {
      const isSelected = select?.ev_type_id === item.ev_type_id
      setSelect(isSelected ? null : item)
      if (!isSelected) {
        setModalValue({ ev_type_id: item.ev_type_id })
      }
    }
  }, [isSelectionMode, select, setModalValue])

  const handleItemClick = useCallback((item) => () => {
    handleSelect(item)
  }, [handleSelect])

  const handleItemDoubleClick = useCallback((item) => () => {
    openModal(item)()
  }, [openModal])

  const acceptConfirmation = useCallback(async () => {
    setIsDeleting(true)
    try {
      await dispatch(deleteEventType(confirmation))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleConfirmSelection = useCallback(() => {
    closeSubModal()
  }, [closeSubModal])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.ev_type_id

  const sortedList = useMemo(() => {
    if (!list) return []
    return [...list].sort((a, b) => a.ev_type?.localeCompare(b.ev_type))
  }, [list])

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiCalendarEvent size={64} className={styles.emptyIcon} />
      <p>Нет добавленных типов событий</p>
      <Button onClick={openModal()}>Добавить первый тип</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка типов событий...</span>
    </div>
  )

  const renderList = () => (
    <div className={styles.listContainer}>
      <ul className={styles.list}>
        {sortedList.map((item) => {
          const isSelected = select?.ev_type_id === item.ev_type_id
          return (
            <Li
              key={item.ev_type_id}
              onClick={handleItemClick(item)}
              onDoubleClick={handleItemDoubleClick(item)}
              className={`${styles.listItem} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.itemContent}>
                {isSelectionMode && isSelected && (
                  <BiCheck size={20} className={styles.checkIcon} />
                )}
                <BiCalendarEvent size={20} className={styles.itemIcon} />
                <span className={styles.itemName}>{item.ev_type}</span>
                {!!item.reporthse && (
                  <span className={styles.badge}>ВШЭ</span>
                )}
              </div>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={handleDelete(item.ev_type_id)}
                title="Удалить тип события"
                aria-label={`Удалить ${item.ev_type}`}
              >
                <BiTrash size={22} />
              </button>
            </Li>
          )
        })}
      </ul>

      <div className={styles.actions}>
        <Button onClick={openModal()}>Добавить</Button>
        {isSelectionMode && select?.ev_type_id && (
          <Button onClick={handleConfirmSelection}>Выбрать</Button>
        )}
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Типы событий</h1>
        {isSelectionMode && (
          <p className={styles.subtitle}>Выберите тип события из списка</p>
        )}
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderList()}
      </main>

      <EventTypeModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title="Уверены что хотите удалить тип события?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}

export default EventTypes
